/**
 * Tableau admin des demandes d'ajout de coéquipier
 * Affiché dans l'onglet "Ajout coéquipier" de ProspectsListPage
 */

import { Link2, Trash2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog, Modal, ModalFooter, toast } from '../ui';
import { Button } from '../ui/button';
import { useCrewJoinRequests, type PotentialDuplicate } from '@/hooks/useCrewJoinRequests';
import { ROUTES } from '@/lib/routes/constants';
import type { CrewJoinRequest } from '@/types/crew-join-request';

// ─── types internes ─────────────────────────────────────────────────────────

type ContactChoice = 'existing' | 'new';

interface DuplicateModalState {
  open: boolean;
  request: CrewJoinRequest | null;
  duplicates: PotentialDuplicate[];
  hasEmailConflict: boolean;
  selectedId: string | null; // ID du contact existant sélectionné
  choice: ContactChoice | null; // 'existing' | 'new'
}

const EMPTY_DUPLICATE_STATE: DuplicateModalState = {
  open: false,
  request: null,
  duplicates: [],
  hasEmailConflict: false,
  selectedId: null,
  choice: null,
};

// ─── sous-composant : carte contact ─────────────────────────────────────────

function ContactCard({
  label,
  data,
  selected,
  selectable,
  onSelect,
}: {
  label: string;
  data: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string | null;
    birth_date?: string | null;
    city?: string | null;
  };
  selected?: boolean;
  selectable?: boolean;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!selectable}
      onClick={onSelect}
      className={[
        'w-full text-left rounded-lg border p-3 transition-colors',
        selectable ? 'cursor-pointer hover:border-zinc-400' : 'cursor-default',
        selected ? 'border-zinc-900 bg-zinc-50 ring-1 ring-zinc-900' : 'border-zinc-200 bg-white',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        {selectable && (
          <div
            className={[
              'mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2',
              selected ? 'border-zinc-900 bg-zinc-900' : 'border-zinc-300 bg-white',
            ].join(' ')}
          />
        )}
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{label}</p>
          <p className="font-medium text-zinc-900">
            {data.first_name} {data.last_name}
          </p>
          <p className="text-sm text-zinc-600">{data.email}</p>
          {data.phone && <p className="text-sm text-zinc-500">{data.phone}</p>}
          <div className="flex gap-3 text-xs text-zinc-400">
            {data.birth_date && (
              <span>Né(e) le {new Date(data.birth_date).toLocaleDateString('fr-FR')}</span>
            )}
            {data.city && <span>{data.city}</span>}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── composant principal ─────────────────────────────────────────────────────

export function CrewJoinRequestsTable({
  onCountChange,
}: {
  onCountChange?: (count: number) => void;
}) {
  const navigate = useNavigate();
  const {
    requests,
    loading,
    fetchAll,
    deleteRequest,
    findPotentialDuplicates,
    attachToCrewAndDelete,
  } = useCrewJoinRequests();

  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    id?: string;
    name?: string;
  }>({ open: false });

  const [attaching, setAttaching] = useState<string | null>(null);
  const [dupState, setDupState] = useState<DuplicateModalState>(EMPTY_DUPLICATE_STATE);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    onCountChange?.(requests.length);
  }, [requests.length, onCountChange]);

  // ── suppression ─────────────────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm.id) return;
    const success = await deleteRequest(deleteConfirm.id);
    if (success) {
      toast.success('Demande supprimée');
      fetchAll();
    } else {
      toast.error('Erreur lors de la suppression');
    }
    setDeleteConfirm({ open: false });
  }, [deleteConfirm.id, deleteRequest, fetchAll]);

  // ── clic rattacher : détection doublons ────────────────────────────────

  const handleAttachClick = useCallback(
    async (request: CrewJoinRequest) => {
      setAttaching(request.id);
      try {
        const result = await findPotentialDuplicates(request);

        if (result.contacts.length > 0) {
          // Doublons trouvés → ouvrir le modal de choix
          setDupState({
            open: true,
            request,
            duplicates: result.contacts,
            hasEmailConflict: result.hasEmailConflict,
            selectedId: result.contacts[0].id, // présélectionner le premier
            choice: 'existing',
          });
          setAttaching(null);
          return;
        }

        // Aucun doublon → rattacher directement
        await doAttach(request, undefined);
      } catch {
        toast.error('Erreur lors de la vérification des doublons');
        setAttaching(null);
      }
    },
    [findPotentialDuplicates] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── confirmation depuis le modal ────────────────────────────────────────

  const handleDuplicateConfirm = useCallback(async () => {
    if (!dupState.request) return;
    const existingId =
      dupState.choice === 'existing' && dupState.selectedId ? dupState.selectedId : undefined;
    setDupState(EMPTY_DUPLICATE_STATE);
    setAttaching(dupState.request.id);
    await doAttach(dupState.request, existingId);
  }, [dupState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── attachement effectif ─────────────────────────────────────────────────

  async function doAttach(request: CrewJoinRequest, existingContactId?: string) {
    try {
      const crewId = await attachToCrewAndDelete(request, existingContactId);
      if (crewId) {
        toast.success("Contact rattaché à l'équipage avec succès");
        navigate(ROUTES.DATA.CREWS.DETAIL(crewId));
      } else {
        toast.error('Erreur lors du rattachement');
      }
    } finally {
      setAttaching(null);
    }
  }

  // ── rendu ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-zinc-500">
        Chargement...
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-sm text-zinc-500 gap-1">
        <p className="font-medium">Aucune demande en attente</p>
        <p className="text-zinc-400">Les demandes d'ajout de coéquipier apparaîtront ici.</p>
      </div>
    );
  }

  const canConfirmDuplicate =
    dupState.choice === 'new' || (dupState.choice === 'existing' && dupState.selectedId !== null);

  return (
    <>
      {/* ── tableau ── */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              {[
                'Équipage',
                'Nom',
                'Prénom',
                'Email',
                'Téléphone',
                'Date de demande',
                'Actions',
              ].map((h, i) => (
                <th
                  key={h}
                  className={[
                    'px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wide',
                    i === 6 ? 'text-right' : 'text-left',
                  ].join(' ')}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {requests.map((request) => (
              <tr key={request.id} className="hover:bg-zinc-50 transition-colors">
                <td className="px-4 py-3 font-medium text-zinc-900">
                  {request.crew?.crew_number ? `#${request.crew.crew_number} ` : ''}
                  {request.crew?.name || <span className="text-zinc-400 italic">Sans nom</span>}
                </td>
                <td className="px-4 py-3 font-semibold text-zinc-900">{request.last_name}</td>
                <td className="px-4 py-3 text-zinc-700">{request.first_name}</td>
                <td className="px-4 py-3 text-zinc-600">{request.email}</td>
                <td className="px-4 py-3 text-zinc-600">{request.phone}</td>
                <td className="px-4 py-3 text-zinc-500">
                  {new Date(request.created_at).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAttachClick(request)}
                      disabled={attaching === request.id}
                      className="flex items-center gap-1.5 text-xs"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      {attaching === request.id ? 'Vérification...' : 'Rattacher'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setDeleteConfirm({
                          open: true,
                          id: request.id,
                          name: `${request.first_name} ${request.last_name}`,
                        })
                      }
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── modal doublons ── */}
      <Modal
        isOpen={dupState.open}
        onClose={() => setDupState(EMPTY_DUPLICATE_STATE)}
        title="Contact similaire détecté"
        size="md"
        footer={
          <ModalFooter
            onCancel={() => setDupState(EMPTY_DUPLICATE_STATE)}
            cancelLabel="Annuler"
            submitLabel="Confirmer"
            onSubmit={handleDuplicateConfirm}
            isSubmitting={false}
            submitDisabled={!canConfirmDuplicate}
          />
        }
      >
        {dupState.request && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              {dupState.hasEmailConflict
                ? 'Un contact avec cette adresse email existe déjà dans la base. Choisissez comment procéder.'
                : 'Un ou plusieurs contacts avec un nom similaire ont été trouvés. Choisissez comment procéder.'}
            </p>

            {/* Données de la demande */}
            <ContactCard label="Demande reçue" data={dupState.request} selectable={false} />

            {/* Séparateur */}
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-zinc-200" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">
                Contact{dupState.duplicates.length > 1 ? 's' : ''} existant
                {dupState.duplicates.length > 1 ? 's' : ''}
              </span>
              <div className="h-px flex-1 bg-zinc-200" />
            </div>

            {/* Liste des contacts en doublon */}
            <div className="space-y-2">
              {dupState.duplicates.map((dup) => (
                <ContactCard
                  key={dup.id}
                  label="Contact existant"
                  data={dup}
                  selectable
                  selected={dupState.choice === 'existing' && dupState.selectedId === dup.id}
                  onSelect={() =>
                    setDupState((s) => ({ ...s, choice: 'existing', selectedId: dup.id }))
                  }
                />
              ))}
            </div>

            {/* Option créer un nouveau contact */}
            <button
              type="button"
              onClick={() => setDupState((s) => ({ ...s, choice: 'new', selectedId: null }))}
              className={[
                'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors cursor-pointer hover:border-zinc-400',
                dupState.choice === 'new'
                  ? 'border-zinc-900 bg-zinc-50 ring-1 ring-zinc-900'
                  : 'border-zinc-200 bg-white',
              ].join(' ')}
            >
              <div
                className={[
                  'h-4 w-4 flex-shrink-0 rounded-full border-2',
                  dupState.choice === 'new'
                    ? 'border-zinc-900 bg-zinc-900'
                    : 'border-zinc-300 bg-white',
                ].join(' ')}
              />
              <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                <UserPlus className="h-4 w-4" />
                Créer un nouveau contact
              </div>
            </button>

            {dupState.hasEmailConflict && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Doublon potentiel : un ou plusieurs contacts avec cette même adresse email existent.
                Vérifiez qu'il ne s'agit pas d'un doublon avant de créer un nouveau contact.
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ── confirmation suppression ── */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open })}
        onConfirm={handleDelete}
        title="Supprimer la demande"
        description={`Voulez-vous vraiment supprimer la demande de "${deleteConfirm.name}" ?`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
      />
    </>
  );
}
